<template>
    <ModalComponent
        :name="'update-descuento-' + voucher.id"
        title="Editar Descuento"
        @close="handleClose"
        @opened="resetForm"
    >
        <div class="tw:space-y-4 tw:py-2">
            <BaseInputText
                v-model="form.name"
                label="Nombre"
                name="name"
                placeholder="Nombre del descuento"
                :required="true"
                :apiValidationError="formErrors.name || null"
            />

            <div>
                <label class="tw:block tw:text-xs tw:uppercase tw:tracking-wide tw:text-slate-600 tw:font-bold tw:mb-2">
                    Código <span class="tw:text-red-500">*</span>
                </label>
                <div class="tw:flex tw:gap-2">
                    <div class="tw:flex-1">
                        <BaseInputText
                            v-model="form.code"
                            name="code"
                            placeholder="Código del cupón"
                            :apiValidationError="formErrors.code || null"
                        />
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        customClass="tw:text-xs tw:shrink-0"
                        @click="generateCode"
                    >
                        <i class="fas fa-dice"></i> Generar
                    </Button>
                </div>
            </div>

            <BaseInputNumber
                v-model="form.units"
                label="Unidades"
                name="units"
                :min="1"
                :step="1"
                :required="true"
                :error="formErrors.units || null"
            />

            <BaseInputNumber
                v-model="form.discount_percent"
                label="Descuento (%)"
                name="discount_percent"
                :min="0"
                :max="100"
                :step="1"
                :disabled="discountFixedFilled"
            />

            <BaseInputNumber
                v-model="form.discount_fixed"
                label="Descuento fijo (€)"
                name="discount_fixed"
                :min="0"
                :step="0.01"
                :disabled="discountPercentFilled"
            />

            <div
                v-if="formErrors.discount"
                class="tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ formErrors.discount }}
            </div>

            <div
                v-if="apiError"
                class="tw:mt-3 tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded tw:text-sm tw:text-red-700"
            >
                {{ apiError }}
            </div>
        </div>

        <template #footer>
            <Button
                type="button"
                variant="ghost"
                class="tw:mr-2"
                @click="handleClose"
                :disabled="saving"
            >
                Cancelar
            </Button>
            <Button
                type="button"
                @click="handleUpdate"
                :disabled="saving"
                :loading="saving"
                id="guardar-descuento"
            >
                Guardar
            </Button>
        </template>
    </ModalComponent>
</template>

<script setup>
    import ModalComponent from '@/modules/admin/ui/ModalComponent.vue';
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue';
    import BaseInputNumber from '@/modules/admin/ui/BaseInputNumber.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import { useEventVouchers } from '@/composables/admin/events/vouchers/useEventVouchers';
    import { useVoucherForm } from '@/composables/admin/events/vouchers/useVoucherForm';

    const props = defineProps({
        eventoId: {
            type: [String, Number],
            required: true,
        },
        voucher: {
            type: Object,
            required: true,
        },
    });

    const emit = defineEmits(['close']);

    const { updateVoucher, validateForm, closeModal, saving, apiError } = useEventVouchers(props.eventoId);

    // Reactive getter — always returns current props when called
    const getInitialValues = () => ({
        name: props.voucher.name || '',
        code: props.voucher.code || '',
        units: props.voucher.units || 1,
        discount_percent: props.voucher.discount_percent ?? null,
        discount_fixed: props.voucher.discount_fixed ?? null,
    });

    const {
        form,
        formErrors,
        discountPercentFilled,
        discountFixedFilled,
        generateCode,
        resetForm,
    } = useVoucherForm(getInitialValues);

    /**
     * Builds a diff object containing only fields that changed from the original.
     */
    function buildDiff() {
        const diff = {};
        const orig = getInitialValues();

        if (form.name !== orig.name) diff.name = form.name;
        if (form.code !== orig.code) diff.code = form.code;
        if (Number(form.units) !== Number(orig.units)) diff.units = form.units;

        const origPercent = orig.discount_percent != null ? Number(orig.discount_percent) : null;
        const currPercent = form.discount_percent != null ? Number(form.discount_percent) : null;
        if (currPercent !== origPercent) diff.discount_percent = form.discount_percent;

        const origFixed = orig.discount_fixed != null ? Number(orig.discount_fixed) : null;
        const currFixed = form.discount_fixed != null ? Number(form.discount_fixed) : null;
        if (currFixed !== origFixed) diff.discount_fixed = form.discount_fixed;

        return diff;
    }

    async function handleUpdate() {
        const errors = validateForm({
            name: form.name,
            code: form.code,
            units: form.units,
            discount_percent: form.discount_percent,
            discount_fixed: form.discount_fixed,
        });

        if (errors) {
            Object.assign(formErrors, errors);
            return;
        }

        const diff = buildDiff();

        if (Object.keys(diff).length === 0) {
            closeModal('update-descuento-' + props.voucher.id);
            return;
        }

        const success = await updateVoucher(props.voucher.id, diff);

        if (success) {
            resetForm();
            closeModal('update-descuento-' + props.voucher.id);
            emit('close', 'update-descuento-' + props.voucher.id);
        }
    }

    function handleClose() {
        resetForm();
        closeModal('update-descuento-' + props.voucher.id);
    }
</script>
